/**
 * SA Tools Report Service
 * Browser-based report generation (Word docs and ROI tables)
 */
import { Chart } from 'chart.js/auto';
import { generateWordDocument } from '../utils/documentGenerator';
import { generateROITableDocument } from '../utils/roiTableGenerator';

/**
 * Load template file from public folder as ArrayBuffer
 */
async function loadTemplate() {
  const response = await fetch('/satools_files/Template.docx');
  if (!response.ok) {
    throw new Error('Failed to load template file');
  }
  const arrayBuffer = await response.arrayBuffer();
  return arrayBuffer;
}

/**
 * Generate prioritization matrix chart as base64 PNG (HIGH QUALITY)
 * Creates scatter plot of Complexity vs Weighted Impact Score
 */
async function generateChartBase64(processes) {
  return new Promise((resolve, reject) => {
    try {
      const complexityScores = [];
      const weightedScores = [];
      const processIds = [];

      processes.forEach((proc, index) => {
        // Map complexity text to numeric value
        const complexityMap = { 'Low': 2, 'Medium': 3, 'High': 4 };
        const complexity = complexityMap[proc.complexity] || 3;

        complexityScores.push(complexity);
        // Use total_score or weighted_score (enriched processes have weighted_score)
        weightedScores.push(parseFloat(proc.total_score || proc.weighted_score || 0));
        processIds.push(proc.id || `PROC${index + 1}`);
      });

      const canvas = document.createElement('canvas');
      canvas.width = 1600;  // High quality
      canvas.height = 1200; // High quality
      const ctx = canvas.getContext('2d');

      new Chart(ctx, {
        type: 'scatter',
        data: {
          datasets: [{
            label: 'Processes',
            data: complexityScores.map((x, i) => ({ x, y: weightedScores[i] })),
            backgroundColor: 'rgba(121, 80, 242, 0.6)',
            borderColor: 'rgba(0, 0, 0, 1)',
            borderWidth: 1,
            pointRadius: 8
          }]
        },
        options: {
          responsive: false,
          animation: false,
          plugins: {
            title: {
              display: true,
              text: 'Process Prioritization Matrix',
              font: { size: 18, weight: 'bold' }
            },
            legend: { display: false }
          },
          scales: {
            x: {
              title: { display: true, text: 'Complexity', font: { size: 14 } },
              min: 1,
              max: 5,
              ticks: {
                callback: function(value) {
                  const labels = { 1: 'Very Low', 2: 'Low', 3: 'Medium', 4: 'High', 5: 'Very High' };
                  return labels[value] || value;
                }
              }
            },
            y: {
              title: { display: true, text: 'Impact to Contact Center', font: { size: 14 } },
              beginAtZero: true
            }
          }
        },
        plugins: [{
          id: 'labels',
          afterDatasetsDraw: (chart) => {
            const ctx = chart.ctx;
            chart.data.datasets[0].data.forEach((point, index) => {
              const meta = chart.getDatasetMeta(0).data[index];
              ctx.fillStyle = 'black';
              ctx.font = 'bold 11px Arial';
              ctx.fillText(processIds[index], meta.x + 5, meta.y - 5);
            });
          }
        }]
      });

      setTimeout(() => {
        const dataUrl = canvas.toDataURL('image/png');
        const base64 = dataUrl.split(',')[1];
        resolve(base64);
      }, 100);
    } catch (err) {
      console.error('Error generating chart:', err);
      reject(err);
    }
  });
}

/**
 * Generate ROI Timeline chart as base64 PNG (HIGH QUALITY)
 * Creates line chart showing cumulative net savings over time
 */
async function generateROITimelineChartBase64(processes, contextData) {
  return new Promise((resolve, reject) => {
    try {
      // Calculate costs
      const professionalServices = contextData?.professional_services_price || 0;
      const bomPrice = contextData?.bom_price || 0;
      const bomLengthMonths = contextData?.bom_length_months || 1;
      const monthlyUsageCosts = contextData?.monthly_usage_costs || 0;
      const costPerAgent = contextData?.cost_per_agent || 25;

      const monthlyBOMCost = bomLengthMonths > 0 ? bomPrice / bomLengthMonths : 0;
      const totalMonthlyCosts = monthlyBOMCost + monthlyUsageCosts;

      // Helper functions
      const parseTimeToSeconds = (timeStr) => {
        if (!timeStr) return 0;
        const numMatch = timeStr.match(/[\d.]+/);
        if (!numMatch) return 0;
        const value = parseFloat(numMatch[0]);
        const lowerStr = timeStr.toLowerCase();
        if (lowerStr.includes('hour') || lowerStr.includes('hr') || lowerStr.includes('h')) {
          return value * 3600;
        } else if (lowerStr.includes('min') || lowerStr.includes('m')) {
          return value * 60;
        } else if (lowerStr.includes('sec') || lowerStr.includes('s')) {
          return value;
        }
        return value;
      };

      const calculateAutomationWithProbability = (probability, automationPotential) => {
        if (!probability || !automationPotential) return 0;
        const probValue = parseFloat(probability) / 100;
        const autoValue = parseFloat(automationPotential) / 100;
        return probValue * autoValue;
      };

      const calculateTotalInteractions = (numberOfInteractions, automationWProbability) => {
        if (!numberOfInteractions || !automationWProbability) return 0;
        const interactions = parseFloat(numberOfInteractions);
        return interactions * automationWProbability;
      };

      const calculateTotalTimeSaved = (totalInteractions, avgTimePerInteraction) => {
        if (!totalInteractions || !avgTimePerInteraction) return 0;
        const timeInSeconds = parseTimeToSeconds(avgTimePerInteraction);
        return totalInteractions * timeInSeconds;
      };

      // Calculate savings by case
      const cases = ['Conservative', 'Likely', 'Optimistic'];
      const defaultProbabilities = {
        'Conservative': '65%',
        'Likely': '80%',
        'Optimistic': '95%'
      };
      const defaultValues = {
        automation_potential: '80%',
        number_of_interactions: '10000',
        avg_time_per_interaction: '30 sec'
      };

      const savingsByCase = {};
      cases.forEach((caseType) => {
        savingsByCase[caseType] = processes.reduce((sum, process) => {
          const caseData = process.roi_cases?.[caseType] || {};
          const automationWProbability = calculateAutomationWithProbability(
            caseData.probability || defaultProbabilities[caseType],
            caseData.automation_potential || defaultValues.automation_potential
          );
          const totalInteractions = calculateTotalInteractions(
            caseData.number_of_interactions || defaultValues.number_of_interactions,
            automationWProbability
          );
          const totalTimeSaved = calculateTotalTimeSaved(
            totalInteractions,
            caseData.avg_time_per_interaction || defaultValues.avg_time_per_interaction
          );
          const timeInHours = parseFloat(totalTimeSaved) / 3600;
          const savings = timeInHours * costPerAgent;
          return sum + savings;
        }, 0);
      });

      // Calculate ROI data
      const roiData = {};
      cases.forEach((caseType) => {
        const netMonthlySavings = savingsByCase[caseType] - totalMonthlyCosts;
        const monthsToPayoff = netMonthlySavings > 0 ? professionalServices / netMonthlySavings : Infinity;
        roiData[caseType] = {
          netMonthlySavings,
          monthsToPayoff: monthsToPayoff < 100 ? monthsToPayoff : 100
        };
      });

      // Generate chart data
      const maxMonths = Math.min(
        Math.max(...cases.map(c => roiData[c].monthsToPayoff < 100 ? roiData[c].monthsToPayoff : 0)) + 6,
        36
      );
      const months = Array.from({ length: Math.ceil(maxMonths) + 1 }, (_, i) => i);

      // Create datasets
      const datasets = cases.map((caseType) => {
        const colors = {
          'Conservative': { bg: 'rgba(239, 68, 68, 0.2)', border: 'rgba(239, 68, 68, 1)' },
          'Likely': { bg: 'rgba(234, 179, 8, 0.2)', border: 'rgba(234, 179, 8, 1)' },
          'Optimistic': { bg: 'rgba(34, 197, 94, 0.2)', border: 'rgba(34, 197, 94, 1)' }
        };

        const data = months.map(month => {
          if (month === 0) return -professionalServices;
          const cumulativeSavings = roiData[caseType].netMonthlySavings * month;
          return cumulativeSavings - professionalServices;
        });

        return {
          label: caseType,
          data: data,
          borderColor: colors[caseType].border,
          backgroundColor: colors[caseType].bg,
          borderWidth: 3,
          fill: false,
          tension: 0.1,
          pointRadius: 0,
          pointHoverRadius: 6
        };
      });

      const canvas = document.createElement('canvas');
      canvas.width = 1600;  // High quality
      canvas.height = 800;  // High quality (2:1 aspect ratio)
      const ctx = canvas.getContext('2d');

      new Chart(ctx, {
        type: 'line',
        data: {
          labels: months,
          datasets: datasets
        },
        options: {
          responsive: false,
          animation: false,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: 'ROI Timeline: Cumulative Net Savings Over Time',
              font: { size: 24, weight: 'bold' }
            },
            legend: {
              display: true,
              position: 'top',
              labels: { font: { size: 16 } }
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  const value = context.parsed.y;
                  return `${context.dataset.label}: $${value.toFixed(2)}`;
                }
              }
            }
          },
          scales: {
            x: {
              title: { display: true, text: 'Months', font: { size: 18, weight: 'bold' } },
              ticks: { stepSize: 3, font: { size: 14 } }
            },
            y: {
              title: { display: true, text: 'Cumulative Net Savings ($)', font: { size: 18, weight: 'bold' } },
              ticks: {
                callback: function(value) {
                  return '$' + value.toLocaleString();
                },
                font: { size: 14 }
              },
              grid: {
                color: function(context) {
                  if (context.tick.value === 0) return 'rgba(0, 0, 0, 0.3)';
                  return 'rgba(0, 0, 0, 0.1)';
                },
                lineWidth: function(context) {
                  if (context.tick.value === 0) return 2;
                  return 1;
                }
              }
            }
          }
        }
      });

      setTimeout(() => {
        const dataUrl = canvas.toDataURL('image/png');
        const base64 = dataUrl.split(',')[1];
        resolve(base64);
      }, 100);
    } catch (err) {
      console.error('Error generating ROI timeline chart:', err);
      reject(err);
    }
  });
}

export const reportService = {
  /**
   * Generate Word document report (browser-based)
   * No server calls - generates entirely in the browser
   * Returns blob for download
   */
  generateReport: async (customerId, contextData, processes) => {
    try {
      console.log('[reportService] Loading template...');
      const templateArrayBuffer = await loadTemplate();
      console.log('[reportService] Template loaded, size:', templateArrayBuffer.byteLength);

      console.log('[reportService] Generating process prioritization chart with', processes.length, 'processes');
      console.log('[reportService] First process:', {
        id: processes[0]?.id,
        name: processes[0]?.name,
        complexity: processes[0]?.complexity,
        total_score: processes[0]?.total_score
      });
      const chartBase64 = await generateChartBase64(processes);
      console.log('[reportService] Process chart generated, base64 length:', chartBase64?.length);

      console.log('[reportService] Generating ROI timeline chart...');
      const roiTimelineBase64 = await generateROITimelineChartBase64(processes, contextData);
      console.log('[reportService] ROI timeline chart generated, base64 length:', roiTimelineBase64?.length);

      console.log('[reportService] Generating Word document in browser...');
      const reportBlob = await generateWordDocument(
        contextData,
        processes,
        chartBase64,
        templateArrayBuffer
      );
      console.log('[reportService] Document generated, blob size:', reportBlob.size);

      // Convert process chart base64 to blob
      const chartByteCharacters = atob(chartBase64);
      const chartByteNumbers = new Array(chartByteCharacters.length);
      for (let i = 0; i < chartByteCharacters.length; i++) {
        chartByteNumbers[i] = chartByteCharacters.charCodeAt(i);
      }
      const chartByteArray = new Uint8Array(chartByteNumbers);
      const chartBlob = new Blob([chartByteArray], { type: 'image/png' });

      // Convert ROI timeline chart base64 to blob
      const roiTimelineByteCharacters = atob(roiTimelineBase64);
      const roiTimelineByteNumbers = new Array(roiTimelineByteCharacters.length);
      for (let i = 0; i < roiTimelineByteCharacters.length; i++) {
        roiTimelineByteNumbers[i] = roiTimelineByteCharacters.charCodeAt(i);
      }
      const roiTimelineByteArray = new Uint8Array(roiTimelineByteNumbers);
      const roiTimelineBlob = new Blob([roiTimelineByteArray], { type: 'image/png' });

      // Create URLs for downloads
      const reportUrl = window.URL.createObjectURL(reportBlob);
      const chartUrl = window.URL.createObjectURL(chartBlob);
      const roiTimelineUrl = window.URL.createObjectURL(roiTimelineBlob);

      return {
        success: true,
        reportUrl,
        chartUrl,
        roiTimelineUrl,
        reportFilename: `IPA_Report_${customerId}.docx`,
        chartFilename: `Process_Prioritization_Chart_${customerId}.png`,
        roiTimelineFilename: `ROI_Timeline_Chart_${customerId}.png`
      };
    } catch (error) {
      console.error('Error generating report:', error);
      throw error;
    }
  },

  /**
   * Generate ROI table as Word document (browser-based)
   * No server calls - generates entirely in the browser
   * Returns URL for download
   */
  generateROITable: async (customerId, contextData, processes) => {
    try {
      console.log('[reportService] Generating ROI table in browser...');

      const blob = await generateROITableDocument(processes, contextData);
      console.log('[reportService] ROI table generated, blob size:', blob.size);

      // Create URL for download
      const url = window.URL.createObjectURL(blob);

      return {
        success: true,
        tableUrl: url,
        tableFilename: `ROI_Analysis_Table_${customerId}.docx`
      };
    } catch (error) {
      console.error('Error generating ROI table:', error);
      throw error;
    }
  }
};

export default reportService;
